import React from "react";
import { motion } from "framer-motion";
import { VOLLEYBALL_PERIODS } from "../../constants/volleyballPeriods";

// Reuse the desktop scoring logic so mobile matches desktop
const getGameScore = (team, currentSet, game) => {
  switch (currentSet) {
    case VOLLEYBALL_PERIODS.S1:
      return game?.periods?.first?.[team] || 0;
    case VOLLEYBALL_PERIODS.S2:
      return game?.periods?.second?.[team] || 0;
    case VOLLEYBALL_PERIODS.S3:
      return game?.periods?.third?.[team] || 0;
    case VOLLEYBALL_PERIODS.S4:
      return game?.periods?.fourth?.[team] || 0;
    case VOLLEYBALL_PERIODS.S5:
      return game?.periods?.fifth?.[team] || 0;
    default:
      return 0;
  }
};

const getLastSet = (game) => {
  const isSet = /S\d/.test(game?.status?.short);
  let lastSet = isSet ? game.status.short : 'S1';

  if (!isSet) {
    const homeScore = typeof game?.scores?.home === 'number' ? game.scores.home : 0;
    const awayScore = typeof game?.scores?.away === 'number' ? game.scores.away : 0;
    const set = homeScore > awayScore ? homeScore : awayScore;
    lastSet = `S${set || 1}`;
  }

  return lastSet;
};

const MobileGameCard = ({ game }) => {
  const lastSet = getLastSet(game);
  const homeSetScore = getGameScore('home', lastSet, game);
  const awaySetScore = getGameScore('away', lastSet, game);

  const hasGameScore = typeof game?.scores?.home === 'number' && typeof game?.scores?.away === 'number';
  const shouldShowSetScore = hasGameScore && game?.status?.short && game.status.short !== 'NS';
  const setScores = hasGameScore ? { home: game.scores.home, away: game.scores.away } : null;
  const pointsToDisplay = shouldShowSetScore ? { home: homeSetScore, away: awaySetScore } : null;
  const teams = [
    { key: 'home', fallback: 'Home' },
    { key: 'away', fallback: 'Away' },
  ];

  const statusLabel = game.status?.long || 'Not Started';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative border border-white/10 p-4 bg-white/[0.03]"
    >
      {/* Header: Country + League (match desktop layout) */}
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-gray-500">
        <div className="flex items-center gap-2">
          <span>{game.country?.code || game.country?.name || 'N/A'}</span>
          {!!game.country?.flag && (
            <img
              src={game.country.flag}
              alt={game.country.name}
              className="h-3 w-5 object-cover rounded-sm"
              loading="lazy"
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          <span>{game.league?.name || 'League'}</span>
          {!!game.league?.logo && (
            <img
              src={game.league.logo}
              alt={game.league.name}
              className="h-4 w-4 object-contain"
              loading="lazy"
            />
          )}
        </div>
      </div>

      {/* Status */}
      <div className="mt-3 flex items-center justify-center text-[10px] font-mono uppercase tracking-widest text-gray-500">
        <span>{statusLabel}</span>
      </div>

      {/* Teams + Scores (Pts / Set) */}
      <div className="flex items-center justify-between mt-3">
        <div className="space-y-3 flex-1">
          {teams.map(({ key, fallback }, index) => {
            const team = game?.teams?.[key];
            return (
              <div key={key} className={`flex items-center gap-3 ${index === 0 ? 'pt-3' : ''}`}>
                {!!team?.logo && (
                  <img
                    src={team.logo}
                    alt={team.name}
                    className="w-9 h-9 object-contain border border-white/10 rounded-sm bg-black/40"
                    loading="lazy"
                  />
                )}
                <div>
                  <p className="text-sm font-mono text-white tracking-tight leading-tight">
                    {team?.name || fallback}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-stretch gap-5 ml-3">
          <div className="text-right">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">Pts</p>
            {teams.map(({ key }, index) => (
              <p key={`${key}-points`} className={`text-xl font-black text-white tabular-nums leading-tight ${index !== 0 ? 'mt-4' : ''}`}>
                {pointsToDisplay ? pointsToDisplay[key] : '--'}
              </p>
            ))}
          </div>
          <span className="w-px bg-white/10" />
          <div className="text-right">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">Set</p>
            {teams.map(({ key }, index) => (
              <p key={`${key}-set`} className={`text-lg font-black text-white tabular-nums leading-tight ${index !== 0 ? 'mt-5' : ''}`}>
                {setScores ? setScores[key] : '--'}
              </p>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MobileGameCard;
